// Pure WAV (PCM 16-bit) helpers for the dubbing block-audio trim.
//
// Framework-free and side-effect free so the byte math is unit-testable without
// a browser. `services/trimmedAudio.js` owns the fetching, decoding and caching
// that uses these.
//
// Why 16-bit PCM rather than re-encoding to a compressed format: the body is
// raw, so trimming to a different length is a byte-range slice plus two header
// fields — O(1) — instead of a per-sample re-encode. That is what keeps a live
// timestamp edit under a millisecond no matter how long the block is.

export const WAV_HEADER_BYTES = 44;
export const BYTES_PER_SAMPLE = 2;

// Channels kept from the source. Block audio from TTS is mono today; a stereo
// source is preserved, anything wider is folded down to the first two channels
// so one odd file cannot multiply the memory budget.
export const MAX_CHANNELS = 2;

const PCM_FORMAT_CODE = 1;
const INT16_MIN_SCALE = 0x8000;
const INT16_MAX_SCALE = 0x7fff;

/** Bytes one frame (one sample across every channel) occupies. */
export const bytesPerFrame = (channelCount) => channelCount * BYTES_PER_SAMPLE;

/**
 * Frame count for `seconds` at `sampleRate`, clamped to what the source holds.
 * Rounding to the nearest frame is what makes the cut land on an exact
 * millisecond: at 48kHz one frame is ~0.02ms.
 */
export const framesForSeconds = (seconds, sampleRate, maxFrames = Infinity) => {
  if (!(seconds > 0) || !(sampleRate > 0)) return 0;
  return Math.max(0, Math.min(maxFrames, Math.round(seconds * sampleRate)));
};

/**
 * A 44-byte canonical WAV header describing `frameCount` frames.
 *
 * The `data` chunk size MUST be the real byte count. `wav-headers@1.0.1`
 * (used by services/audio.js) defaults it to ~4GB when the caller omits
 * `dataLength`, which leaves the browser reporting a bogus `duration` and
 * firing `ended` unreliably — the very failure this module exists to avoid.
 */
export const buildWavHeader = ({ sampleRate, channelCount, frameCount }) => {
  const blockAlign = bytesPerFrame(channelCount);
  const dataBytes = frameCount * blockAlign;
  const header = new ArrayBuffer(WAV_HEADER_BYTES);
  const view = new DataView(header);
  const writeText = (offset, text) => {
    for (let i = 0; i < text.length; i += 1) {
      view.setUint8(offset + i, text.charCodeAt(i));
    }
  };

  writeText(0, 'RIFF');
  view.setUint32(4, WAV_HEADER_BYTES - 8 + dataBytes, true);
  writeText(8, 'WAVE');

  writeText(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, PCM_FORMAT_CODE, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, BYTES_PER_SAMPLE * 8, true);

  writeText(36, 'data');
  view.setUint32(40, dataBytes, true);
  return header;
};

/**
 * Interleaved 16-bit PCM for the first `frameCount` frames of `audioBuffer`.
 * Returns the body only — no header — so the same bytes can back many different
 * cut lengths.
 */
export const encodePcm16 = (audioBuffer, frameCount, channelCount) => {
  const frames = Math.min(frameCount, audioBuffer.length);
  const body = new ArrayBuffer(frames * bytesPerFrame(channelCount));
  const view = new DataView(body);
  const channels = [];
  for (let channel = 0; channel < channelCount; channel += 1) {
    channels.push(audioBuffer.getChannelData(channel));
  }

  let offset = 0;
  for (let frame = 0; frame < frames; frame += 1) {
    for (let channel = 0; channel < channelCount; channel += 1) {
      const sample = Math.max(-1, Math.min(1, channels[channel][frame]));
      const scaled =
        sample < 0 ? sample * INT16_MIN_SCALE : sample * INT16_MAX_SCALE;
      view.setInt16(offset, scaled, true);
      offset += BYTES_PER_SAMPLE;
    }
  }
  return body;
};

/**
 * Ramp `tail` (the last frames of a clip) linearly down to silence, in place.
 *
 * Almost every dubbing block is cut mid-speech — the slot covers only the
 * spoken part of the transcript while the synthesized audio carries pauses on
 * top — so a hard cut would click on essentially every block. The ramp lives
 * INSIDE the played window, so the audible clip still ends exactly at `endTime`.
 *
 * Takes the tail bytes rather than the whole clip so the cost stays proportional
 * to the fade (a few hundred bytes), not to the clip.
 */
export const applyFadeOut = (tail, channelCount) => {
  const view = new DataView(tail);
  const frames = tail.byteLength / bytesPerFrame(channelCount);
  if (frames <= 0) return tail;

  for (let frame = 0; frame < frames; frame += 1) {
    const gain = 1 - (frame + 1) / frames;
    for (let channel = 0; channel < channelCount; channel += 1) {
      const offset = (frame * channelCount + channel) * BYTES_PER_SAMPLE;
      view.setInt16(
        offset,
        Math.round(view.getInt16(offset, true) * gain),
        true,
      );
    }
  }
  return tail;
};

/** Channels this module will keep from a decoded buffer. */
export const usableChannelCount = (audioBuffer) =>
  Math.max(1, Math.min(audioBuffer.numberOfChannels, MAX_CHANNELS));
